import { Frequency } from '@generated/graphql';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { US_DATE_FORMAT } from '@ovComponents/4-module/workflowCompletion/constants/dateFormats';
import {
  DEPOSIT_TRANSFER_TYPE,
  type DepositTransferType,
} from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import {
  addBusinessDays,
  isBusinessDayAndAllowWeekendSync,
  isBusinessDaySync,
} from '@utils/businessDays';
import dayjs, { type ConfigType } from 'dayjs';
import utc from 'dayjs/plugin/utc';

export enum DepositStarting {
  Now = 'now',
  FutureDate = 'futureDate',
}

export type DepositScheduleOptions = {
  allowOneTimeTransfersNow?: boolean;
  allowOneTimeTransfersFutureDate?: boolean;
  transferDelay?: { enabled?: boolean; number?: number } | null;
  editDefaultScheduleFrequency?: boolean;
  schedule?: { enabled?: boolean } | null;
  defaultScheduleOption?: Frequency | null;
  scheduledDate?: {
    enabled?: boolean;
    required?: FieldRequiredType | string | null;
  } | null;
};

export type TransferDelayCheck = {
  dateIsAfter: boolean;
  minTransferDate: string;
};

export type ScheduledDateErrorMessage = {
  key:
    | 'bankAccountTransferCreation.transferDelayError'
    | 'bankAccountTransferCreation.enterAValidDate';
  params?: { date: string };
};

export type ScheduleFieldsVisibility = {
  schedule: boolean;
  starting: boolean;
  startingOptions: DepositStarting[];
  scheduledDate: boolean;
  transferDelayAlert: boolean;
};

export const SCHEDULE_FREQUENCIES = [
  Frequency.OneTime,
  Frequency.Weekly,
  Frequency.BiWeekly,
  Frequency.SemiMonthly,
  Frequency.Monthly,
  Frequency.Quarterly,
];

export const TRANSFER_TYPES_WITHOUT_SCHEDULE: DepositTransferType[] = [
  DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER,
  DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER,
];

const HIDDEN_SCHEDULE_FIELDS: ScheduleFieldsVisibility = {
  schedule: false,
  starting: false,
  startingOptions: [],
  scheduledDate: false,
  transferDelayAlert: false,
};

// `@utils/businessDays` also registers utc, but tests mock that module.
dayjs.extend(utc);

const resolveStartingOptions = ({
  options,
  isInitiatedByRepresentative,
}: {
  options?: DepositScheduleOptions | null;
  isInitiatedByRepresentative: boolean;
}) => [
  ...(options?.allowOneTimeTransfersNow && !isInitiatedByRepresentative
    ? [DepositStarting.Now]
    : []),
  ...(options?.allowOneTimeTransfersFutureDate
    ? [DepositStarting.FutureDate]
    : []),
];

export const shouldDefaultToFutureDate = ({
  deposit,
  options,
}: {
  deposit: Pick<TransferContextData, 'type' | 'scheduledDate'>;
  options?: DepositScheduleOptions | null;
}) => {
  if (!options?.allowOneTimeTransfersFutureDate) return false;
  if (deposit.type !== DEPOSIT_TRANSFER_TYPE.EFT) return false;
  if (deposit.scheduledDate) return true;
  if (options?.transferDelay?.enabled) return true;
  if (
    options?.allowOneTimeTransfersFutureDate &&
    !options?.allowOneTimeTransfersNow
  )
    return true;
  return false;
};

export const checkTransferDelay = async ({
  scheduledDate,
  days,
  country,
}: {
  scheduledDate?: Date | string;
  days: number | undefined;
  country: string;
}): Promise<TransferDelayCheck> => {
  const today = new Date();
  const minTransferDate = await addBusinessDays(today, days ?? 0, country);

  return {
    dateIsAfter:
      dayjs(scheduledDate).isSame(minTransferDate, 'day') ||
      dayjs(scheduledDate).isAfter(minTransferDate),
    minTransferDate: dayjs(minTransferDate).format(US_DATE_FORMAT),
  };
};

export const isValidTransferDelayCheck = ({
  isEft,
  isScheduled,
  options,
  transferDelayCheck,
  scheduledDateIsBusinessDay,
}: {
  isEft: boolean;
  isScheduled: boolean;
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
  scheduledDateIsBusinessDay: boolean | null;
}) => {
  const transferDelayApplies =
    isEft && isScheduled && !!options?.transferDelay?.enabled;
  if (!transferDelayApplies) return true;

  return (
    !!transferDelayCheck &&
    transferDelayCheck.dateIsAfter &&
    scheduledDateIsBusinessDay === true
  );
};

export const resolveScheduledDateError = ({
  transferDate,
  options,
  transferDelayCheck,
  scheduledDateIsBusinessDay,
}: {
  transferDate?: TransferContextData['scheduledDate'];
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
  scheduledDateIsBusinessDay: boolean | null;
}) =>
  (!transferDate &&
    options?.scheduledDate?.required !== FieldRequiredType.NOT_REQUIRED) ||
  scheduledDateIsBusinessDay === false ||
  (options?.transferDelay?.enabled &&
    !!transferDelayCheck &&
    !transferDelayCheck.dateIsAfter);

export const resolveScheduledDateErrorMessage = ({
  options,
  transferDelayCheck,
}: {
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
}): ScheduledDateErrorMessage => {
  if (
    options?.transferDelay?.enabled &&
    transferDelayCheck &&
    !transferDelayCheck.dateIsAfter
  ) {
    const date = dayjs(transferDelayCheck.minTransferDate).format(
      US_DATE_FORMAT,
    );
    return {
      key: 'bankAccountTransferCreation.transferDelayError',
      params: { date },
    };
  }
  return { key: 'bankAccountTransferCreation.enterAValidDate' };
};

export const resolveDefaultScheduleOption = (
  options?: DepositScheduleOptions | null,
) =>
  options?.editDefaultScheduleFrequency && options?.defaultScheduleOption
    ? options?.defaultScheduleOption
    : undefined;

export const shouldClearScheduledDate = ({
  date,
  options,
  newDelayCheck,
}: {
  date: ConfigType;
  options?: DepositScheduleOptions | null;
  newDelayCheck: TransferDelayCheck;
}) => {
  const isBeforeToday = dayjs(date).isBefore(dayjs(), 'day');
  const failsTransferDelay =
    options?.transferDelay?.enabled && !newDelayCheck.dateIsAfter;

  return isBeforeToday || failsTransferDelay;
};

export const resolveScheduledDateMinDate = ({
  options,
  transferDelayCheck,
}: {
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
}) =>
  options?.transferDelay?.enabled
    ? (transferDelayCheck?.minTransferDate ?? dayjs().add(1, 'day'))
    : dayjs().add(1, 'day');

export const isScheduledDateEditable = ({
  date,
  schedule,
  weekdaysOnlyCalendar,
  transferCountry,
}: {
  date: ConfigType;
  schedule?: Frequency | null;
  weekdaysOnlyCalendar: boolean;
  transferCountry: string;
}) => {
  const dayjsDate = dayjs(date);
  const dayOfMonth = dayjsDate.date();

  // For SEMI_MONTHLY, only allow 1st and 15th
  if (schedule === Frequency.SemiMonthly) {
    return dayOfMonth === 1 || dayOfMonth === 15;
  }

  // For other schedules, use business day logic
  return weekdaysOnlyCalendar
    ? isBusinessDaySync({
        date: dayjsDate.utc().toDate(),
        country: transferCountry,
      })
    : isBusinessDayAndAllowWeekendSync({
        date: dayjsDate.utc().toDate(),
        country: transferCountry,
      });
};

export const resolveScheduleFieldsVisibility = ({
  deposit,
  starting,
  options,
  canWriteTransferOboClients,
  canWriteTransferBasic,
}: {
  deposit?: Pick<TransferContextData, 'type' | 'schedule'> | null;
  starting: DepositStarting;
  options?: DepositScheduleOptions | null;
  canWriteTransferOboClients: boolean;
  canWriteTransferBasic: boolean;
}): ScheduleFieldsVisibility => {
  if (deposit?.type !== DEPOSIT_TRANSFER_TYPE.EFT) {
    return HIDDEN_SCHEDULE_FIELDS;
  }

  const isOneTime = deposit.schedule === Frequency.OneTime;
  const startsNow = starting === DepositStarting.Now;

  return {
    schedule: !!options?.schedule?.enabled,
    starting:
      isOneTime &&
      (!!options?.allowOneTimeTransfersNow ||
        !!options?.allowOneTimeTransfersFutureDate),
    startingOptions: resolveStartingOptions({
      options,
      isInitiatedByRepresentative:
        canWriteTransferOboClients && !canWriteTransferBasic,
    }),
    scheduledDate: isOneTime ? !startsNow : !!options?.scheduledDate?.enabled,
    transferDelayAlert:
      !!options?.transferDelay?.enabled && !(isOneTime && startsNow),
  };
};
